import { CSSProperties } from '@apia/theme';
import { StatefulEmitter } from '@apia/util';

type LockTypes = 'linear' | 'white' | 'common';
type Lock = {
  count: number;
};
type LockOptions = Partial<{
  type: LockTypes;
}>;

export class ScreenLocker {
  protected emitter = new StatefulEmitter<{
    locks: Record<LockTypes, Lock>;
    release: true;
  }>({
    locks: {
      common: { count: 0 },
      linear: { count: 0 },
      white: { count: 1 },
    },
  });

  lock = (options?: LockOptions) => {
    this.emitter.setState('locks', (s) => {
      const key = options?.type ?? 'common';

      return {
        ...s,
        [key]: { count: (s[key].count ?? 0) + 1 },
      };
    });

    return () =>
      this.emitter.setState('locks', (s) => {
        const key = options?.type ?? 'common';

        return {
          ...s,
          [key]: { count: (s[key].count ?? 0) - 1 },
        };
      });
  };

  protected hasReleased = false;
  /**
   * This method will work once, then, the action will be ignored. The ScreenLocker starts always locked on white, when this method is called, all listeners of onRelease will be called and the lock will be released.
   */
  release = () => {
    if (this.hasReleased) return;
    this.hasReleased = true;

    this.emitter.setState('locks', (s) => {
      const key = 'white';

      return {
        ...s,
        [key]: { count: (s[key].count ?? 0) - 1 },
      };
    });
    this.emitter.emit('release', true);
  };

  /**
   * This method notifies when the screenLock is released by the first time.
   */
  onRelease = this.emitter.on.bind(this.emitter, 'release');

  /**
   * This component is the responsible for putting the lock courtain in the browser when it's required. It must be used once per application.
   */
  Component = () => {
    const locks = this.emitter.useState('locks');

    const style: CSSProperties = {
      alignItems: 'center',
      display: 'flex',
      inset: 0,
      justifyContent: 'center',
      position: 'fixed',
    };

    if (locks.white.count) {
      return (
        <div
          style={{
            ...style,
            background: 'white',
          }}
        >
          White
        </div>
      );
    }

    if (locks.linear.count) {
      return <div style={style}>Linear</div>;
    }

    if (locks.common.count) {
      return <div style={style}>Common</div>;
    }

    return null;
  };
}
